import {Text, createStyles} from '@mantine/core';
import React, {useMemo} from 'react';

import EntityInputChip from '../lib/EntityInputChip';
import FileInputChip from '../lib/FileInputChip';
import InputChip from '../lib/InputChip';
import StructureTextInputChip from '../lib/StructureTextInputChip';
import {parsePredictionInputs} from './predictionInputs';
import type {PredictionJobComponent} from './PredictionJobTypes';

interface InputChipsProps {
  configJson: Record<string, unknown>;
  components: ReadonlyArray<PredictionJobComponent>;
}

/** Renders the parsed inputs from a prediction job's `configJson` as a row of chips. */
export function InputChips({configJson, components}: InputChipsProps): JSX.Element {
  const {classes} = useStyles();
  const inputs = useMemo(() => parsePredictionInputs(configJson, components), [configJson, components]);

  if (inputs.length === 0) {
    return (
      <Text color="dimmed" size="sm">
        —
      </Text>
    );
  }

  return (
    <div className={classes.inputChips}>
      {inputs.map((input, index) => {
        switch (input.type) {
          case 'entity':
            return <EntityInputChip entityId={input.entityId} key={`${input.entityId}-${index}`} />;
          case 'file':
            return (
              <FileInputChip
                fileId={input.fileId}
                fileVersionId={input.fileVersionId}
                key={`${input.fileId}-${index}`}
              />
            );
          case 'structureText':
            return (
              <StructureTextInputChip
                iconOnly={true}
                key={`structureText-${index}`}
                structure={input.structure}
              />
            );
          default:
            return (
              <InputChip
                key={`${input.type}-${index}`}
                label={input.name}
                tooltip={true}
                type={input.type}
                value={input.value}
              />
            );
        }
      })}
    </div>
  );
}

const useStyles = createStyles(() => ({
  inputChips: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 4,
  },
}));
